/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  getCronValidationMessage,
  isURL,
} from '@agilelab/plugin-wb-platform-common';
import { ApiHolder } from '@backstage/core-plugin-api';
import { FieldValidation, FormValidation } from '@rjsf/utils';
import { AccessControlListPicker } from '../components/fields/AccessControlListPicker';
import {
  ComponentIdentifierPicker,
  componentIdentifierPickerValidation,
} from '../components/fields/ComponentIdentifierPicker';
import { CronExpressionPicker } from '../components/fields/CronExpressionPicker/CronExpressionPicker';
import { customUrlPickerValidation } from '../components/fields/CustomUrlPicker';
import { CustomUrlPicker } from '../components/fields/CustomUrlPicker/CustomUrlPicker';
import { DescriptorPicker } from '../components/fields/DescriptorPicker';
import { EntityNamePicker } from '../components/fields/EntityNamePicker';
import { EntityPicker } from '../components/fields/EntityPicker/EntityPicker';
import { EntityRelationsPicker } from '../components/fields/EntityRelationsPicker/EntityRelationsPicker';
import { EntitySearchPicker } from '../components/fields/EntitySearchPicker/EntitySearchPicker';
import { EntitySelectionPicker } from '../components/fields/EntitySelectionPicker/EntitySelectionPicker';
import { EntityTagsPicker } from '../components/fields/EntityTagsPicker/EntityTagsPicker';
import { FilePicker } from '../components/fields/FilePicker';
import {
  IdentifierPicker,
  identifierPickerValidation,
} from '../components/fields/IdentifierPicker';
import { IdentitiesPicker } from '../components/fields/IdentitiesPicker';
import { OwnedEntityPicker } from '../components/fields/OwnedEntityPicker';
import { OwnerPicker } from '../components/fields/OwnerPicker/OwnerPicker';
import { RbacRolePicker } from '../components/fields/RbacRolePicker';
import { ReadsFromPicker } from '../components/fields/ReadsFromPicker/ReadsFromPicker';
import { RegexPicker } from '../components/fields/RegexPicker/RegexPicker';
import { regExPickerValidation } from '../components/fields/RegexPicker/validation';
import { RepoUrlPicker } from '../components/fields/RepoUrlPicker/RepoUrlPicker';
import { repoPickerValidation } from '../components/fields/RepoUrlPicker/validation';
import { WittyTextPicker } from '../components/fields/WittyTextPicker/WittyTextPicker';
import { StarPicker } from '../components/fields/StarPicker/StarPicker';
import { PrototypeStatusPicker } from '../components/fields/PrototypeStatusPicker/PrototypeStatusPicker';
import { CustomFieldValidator, FieldExtensionOptions } from './types';

export const DEFAULT_SCAFFOLDER_FIELD_EXTENSIONS: FieldExtensionOptions<
  any,
  any
>[] = [
  {
    component: CronExpressionPicker,
    name: 'CronExpressionPicker',
    asyncValidation: false,
    validation: ((value: string, validation: FieldValidation) => {
      if (value && !!getCronValidationMessage(value).message) {
        validation.addError('');
        return;
      }
    }) as CustomFieldValidator<string>,
  },
  {
    component: EntitySearchPicker,
    name: 'EntitySearchPicker',
  },
  {
    component: EntityPicker,
    name: 'EntityPicker',
  },
  {
    component: EntityNamePicker,
    name: 'EntityNamePicker',
  },
  {
    component: EntityTagsPicker,
    name: 'EntityTagsPicker',
  },
  {
    component: RepoUrlPicker,
    name: 'RepoUrlPicker',
    validation: repoPickerValidation as CustomFieldValidator<string>,
    asyncValidation: false,
  },
  {
    component: OwnerPicker,
    name: 'OwnerPicker',
  },
  {
    component: OwnedEntityPicker,
    name: 'OwnedEntityPicker',
  },
  {
    component: IdentifierPicker,
    name: 'IdentifierPicker',
    validation: identifierPickerValidation as CustomFieldValidator<string>,
    asyncValidation: true,
  },
  {
    component: ComponentIdentifierPicker,
    name: 'ComponentIdentifierPicker',
    validation:
      componentIdentifierPickerValidation as CustomFieldValidator<string>,
    asyncValidation: true,
  },
  {
    component: EntitySelectionPicker,
    name: 'EntitySelectionPicker',
  },
  {
    component: EntityRelationsPicker,
    name: 'EntityComponentsPicker',
  },
  {
    component: EntityRelationsPicker,
    name: 'EntityRelationsPicker',
  },
  {
    component: ReadsFromPicker,
    name: 'ReadsFromPicker',
  },
  {
    component: RbacRolePicker,
    name: 'RbacRolePicker',
  },
  {
    component: RegexPicker,
    name: 'PathPicker',
    validation: ((
      value: any,
      validation: FormValidation,
      context: { apiHolder: ApiHolder },
      _propSchema: any,
      formContext: any,
      fieldName?: string,
    ) =>
      regExPickerValidation(
        value,
        validation,
        context,
        {
          validation: {
            regularExpression: `^(([/\\w-_]+)+/)?([\\w-_]+\\.\\w+)?$|^(([a-zA-Z]:|\\\\[\\w\\d-]+\\\\[\\w\\d-]+)\\\\|(.\\\\|\\\\)?)(((.)|(\\.\\.)|([\\w\\d-]+))\\\\)*([\\w\\d-]+)?$`,
            errorMessage: 'Must be a valid path',
            flags: '',
          },
        },
        formContext,
        fieldName,
      )) as CustomFieldValidator<any>,
    asyncValidation: false,
  },
  {
    component: RegexPicker,
    name: 'UrlPicker',
    validation: ((
      value: any,
      validation: FieldValidation,
      _context: { apiHolder: ApiHolder },
      _propSchema: any,
      _formContext: any,
    ) => {
      if (value && !isURL(value)) {
        validation.addError('Must be a valid URL (http or https only).');
        return;
      }
    }) as CustomFieldValidator<any>,
    asyncValidation: false,
  },
  {
    component: RegexPicker,
    name: 'RegexPicker',
    validation: regExPickerValidation as CustomFieldValidator<any>,
    asyncValidation: false,
  },
  {
    component: CustomUrlPicker,
    name: 'CustomUrlPicker',
    validation: customUrlPickerValidation as CustomFieldValidator<any>,
    asyncValidation: true,
  },
  {
    component: DescriptorPicker,
    name: 'DescriptorPicker',
    asyncValidation: false,
  },
  {
    component: FilePicker,
    name: 'FilePicker',
  },
  {
    component: IdentitiesPicker,
    name: 'IdentitiesPicker',
    asyncValidation: false,
    validation: ((
      value: string[] | undefined,
      validation: FieldValidation,
      _context: { apiHolder: ApiHolder },
      configuration: any,
    ) => {
      if (
        configuration?._isRequired &&
        (!Array.isArray(value) || value.length === 0)
      ) {
        validation.addError('You must select at least one item.');
      }
    }) as CustomFieldValidator<any>,
  },
  {
    component: AccessControlListPicker,
    name: 'AccessControlListPicker',
    asyncValidation: false,
  },
  {
    component: WittyTextPicker,
    name: 'WittyTextPicker',
    asyncValidation: true,
  },
  {
    component: StarPicker,
    name: 'StarPicker',
  },
  {
    component: PrototypeStatusPicker,
    name: 'PrototypeStatusPicker',
  },
];
